/**
 * Avenue Perfumes — language + currency switcher
 *
 * The pill opens a panel; the panel is a real Shopify /localization form.
 * Choosing a language or a country only moves the tick — nothing is sent until
 * Save, so one reload applies both, the way Transcy's widget behaved.
 *
 * Three rules keep the shopper's choice from drifting:
 *
 *   1. Only the field the shopper actually CHANGED is submitted. Shopify's own
 *      theme uses two separate forms for exactly this reason — sending
 *      locale_code on every save means a stale value (a page served from cache
 *      under the wrong locale, a bfcache restore, a redirect that landed on the
 *      other language) silently re-sets the language. Unchanged inputs are
 *      disabled, so they are never sent and Shopify leaves that setting alone.
 *
 *   2. The current language is read from the URL, not from what Liquid printed
 *      into the page. In Shopify the /xx/ prefix is what actually decides the
 *      locale, so the URL is the one source that cannot be stale. return_to is
 *      rewritten to the chosen language's path before submitting, which pins
 *      the redirect too.
 *
 *   3. The country is remembered in localStorage as well as in Shopify's
 *      cookie, because that cookie can be dropped — by a cached response, a
 *      geolocation prompt, a fresh session — and the currency would fall back
 *      to AED partway through a visit. The remembered value is what the pill
 *      and the ticks show; saving re-asserts it to Shopify whenever the two
 *      have drifted apart, so shipping stays correct too.
 */
(function () {
  'use strict';

  var MOBILE = '(max-width: 767px)';
  var STORAGE_KEY = 'ap_country';

  function remembered() {
    try {
      return window.localStorage.getItem(STORAGE_KEY);
    } catch (e) {
      return null;
    }
  }

  function remember(value) {
    try {
      window.localStorage.setItem(STORAGE_KEY, value);
    } catch (e) { /* private mode — Shopify's cookie still carries it */ }
  }

  function init(root) {
    if (root.dataset.apLocReady) return;
    root.dataset.apLocReady = '1';

    var toggle = root.querySelector('[data-ap-loc-toggle]');
    var panel = root.querySelector('[data-ap-loc-panel]');
    var backdrop = root.querySelector('.ap-loc__backdrop');
    var form = root.querySelector('form');
    if (!toggle || !panel || !form) return;

    var localeInput = form.querySelector('[data-ap-loc-locale]');
    var countryInput = form.querySelector('[data-ap-loc-country]');
    var returnInput = form.querySelector('input[name="return_to"]');

    var locales = (root.getAttribute('data-ap-locales') || '').split(',');
    var rootLocale = root.getAttribute('data-ap-root-locale') || 'en';

    // --- what is actually current -------------------------------------------

    function localeFromPath() {
      var first = window.location.pathname.split('/')[1];
      return locales.indexOf(first) > -1 ? first : rootLocale;
    }

    function isOfferedCountry(code) {
      return !!(code && panel.querySelector('[data-ap-group="country"][data-ap-value="' + code + '"]'));
    }

    var currentLocale = localeFromPath();

    // Shopify's answer, and the shopper's remembered answer. They agree most
    // of the time; when they do not, the shopper's wins on screen and gets
    // pushed back to Shopify on the next save.
    var shopifyCountry = countryInput ? countryInput.value : '';
    var storedCountry = remembered();
    var currentCountry = isOfferedCountry(storedCountry) ? storedCountry : shopifyCountry;

    var chosenLocale = currentLocale;
    var chosenCountry = currentCountry;

    if (localeInput) localeInput.value = currentLocale;

    function syncTicks(group, value) {
      var options = panel.querySelectorAll('[data-ap-group="' + group + '"]');
      Array.prototype.forEach.call(options, function (option) {
        var on = option.getAttribute('data-ap-value') === value;
        option.classList.toggle('is-selected', on);
        option.setAttribute('aria-checked', on ? 'true' : 'false');
      });
    }

    // If the page was rendered under a different locale than the URL says, or
    // Shopify forgot the country, the ticks would be pointing at the wrong
    // rows. Put them where they belong before the shopper opens the panel.
    syncTicks('language', currentLocale);
    syncTicks('country', currentCountry);

    // --- where Save should land ---------------------------------------------

    function pathWithoutLocale() {
      var path = window.location.pathname;
      var first = path.split('/')[1];
      if (locales.indexOf(first) > -1) path = path.slice(first.length + 1);
      if (path.charAt(0) !== '/') path = '/' + path;
      return path;
    }

    function returnPathFor(locale) {
      var base = pathWithoutLocale();
      var prefixed = locale === rootLocale ? base : '/' + locale + base;
      return prefixed + window.location.search;
    }

    // --- open / close --------------------------------------------------------

    // The sticky header is a transformed ancestor, and `position: fixed` is
    // trapped inside one of those — the centred phone sheet would be positioned
    // against the header instead of the viewport. So on phones the panel is
    // moved to <body> while it is open and put back afterwards.
    var anchor = document.createComment('ap-loc-panel');
    var portaled = false;

    function isMobile() {
      return window.matchMedia && window.matchMedia(MOBILE).matches;
    }

    function portalOut() {
      if (portaled || !isMobile()) return;
      panel.parentNode.insertBefore(anchor, panel);
      document.body.appendChild(panel);
      if (backdrop) document.body.appendChild(backdrop);
      portaled = true;
    }

    function portalBack() {
      if (!portaled) return;
      if (anchor.parentNode) {
        anchor.parentNode.insertBefore(panel, anchor);
        if (backdrop) anchor.parentNode.insertBefore(backdrop, panel);
        anchor.parentNode.removeChild(anchor);
      }
      portaled = false;
    }

    function onDocumentClick(event) {
      if (!root.contains(event.target) && !panel.contains(event.target)) close();
    }

    function onKeydown(event) {
      if (event.key === 'Escape' || event.key === 'Esc') {
        close();
        toggle.focus();
      }
    }

    function open() {
      portalOut();
      panel.hidden = false;
      if (backdrop) backdrop.hidden = false;
      root.classList.add('is-open');
      toggle.setAttribute('aria-expanded', 'true');
      document.addEventListener('click', onDocumentClick, true);
      document.addEventListener('keydown', onKeydown);

      var selected = panel.querySelector('.ap-loc__option.is-selected');
      if (selected) {
        try { selected.focus({ preventScroll: true }); } catch (e) { selected.focus(); }
      }
    }

    function close() {
      panel.hidden = true;
      if (backdrop) backdrop.hidden = true;
      root.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
      document.removeEventListener('click', onDocumentClick, true);
      document.removeEventListener('keydown', onKeydown);
      portalBack();

      // Reopening should start from what is live, not from a half-made choice.
      chosenLocale = currentLocale;
      chosenCountry = currentCountry;
      syncTicks('language', chosenLocale);
      syncTicks('country', chosenCountry);
    }

    toggle.addEventListener('click', function () {
      if (panel.hidden) open(); else close();
    });

    Array.prototype.forEach.call(
      root.querySelectorAll('[data-ap-loc-close]'),
      function (el) { el.addEventListener('click', close); }
    );

    // --- choosing ------------------------------------------------------------

    Array.prototype.forEach.call(
      root.querySelectorAll('[data-ap-group]'),
      function (option) {
        option.addEventListener('click', function () {
          var group = option.getAttribute('data-ap-group');
          var value = option.getAttribute('data-ap-value');

          if (group === 'language') chosenLocale = value;
          else chosenCountry = value;

          syncTicks(group, value);
        });
      }
    );

    // --- saving --------------------------------------------------------------

    var submitting = false;

    form.addEventListener('submit', function (event) {
      // A double tap on a slow connection would otherwise fire twice. The
      // button is deliberately NOT disabled: a disabled button that never gets
      // re-enabled is worse than a duplicate request.
      if (submitting) {
        event.preventDefault();
        return;
      }
      submitting = true;
      window.setTimeout(function () { submitting = false; }, 4000);

      var languageChanged = chosenLocale !== currentLocale;

      // Compared against SHOPIFY's value, not the shopper's remembered one, so
      // that a save also repairs a country Shopify has forgotten.
      var countryChanged = chosenCountry !== shopifyCountry;

      remember(chosenCountry);

      // A disabled field is not submitted, so Shopify never sees a setting the
      // shopper did not touch — and therefore never changes it.
      if (localeInput) {
        localeInput.value = chosenLocale;
        localeInput.disabled = !languageChanged;
      }
      if (countryInput) {
        countryInput.value = chosenCountry;
        countryInput.disabled = !countryChanged;
      }
      if (returnInput) {
        returnInput.value = returnPathFor(chosenLocale);
      }

      // Nothing for Shopify to do, but the shopper still pressed Save and the
      // remembered currency may have changed. Reload so the page repaints.
      if (!languageChanged && !countryChanged) {
        event.preventDefault();
        submitting = false;
        close();
        window.location.reload();
      }
    });

    // --- keyboard ------------------------------------------------------------

    panel.addEventListener('keydown', function (event) {
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

      var current = document.activeElement;
      if (!current || !current.hasAttribute('data-ap-group')) return;

      var group = current.getAttribute('data-ap-group');
      var options = Array.prototype.slice.call(
        panel.querySelectorAll('[data-ap-group="' + group + '"]')
      );
      var index = options.indexOf(current);
      if (index < 0) return;

      event.preventDefault();
      var next = event.key === 'ArrowDown' ? index + 1 : index - 1;
      if (next < 0) next = options.length - 1;
      if (next >= options.length) next = 0;
      options[next].focus();
      options[next].click();
    });

    window.addEventListener('resize', function () {
      if (!panel.hidden && portaled !== isMobile()) close();
    });

    // Back button restores the page from bfcache with the inputs still disabled
    // from the last submit. Put everything back the way a fresh load has it.
    window.addEventListener('pageshow', function (event) {
      if (!event.persisted) return;
      submitting = false;
      if (localeInput) localeInput.disabled = false;
      if (countryInput) countryInput.disabled = false;
      currentLocale = localeFromPath();
      chosenLocale = currentLocale;
      syncTicks('language', currentLocale);
    });
  }

  function boot() {
    Array.prototype.forEach.call(
      document.querySelectorAll('[data-ap-loc]'),
      init
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
